import { apiClient } from './client';
import type { UIProductCategory } from '../../types/product';

export interface ProductType {
  type: string;
  name: string;
  category: string;
  description: string;
}

export interface ProductParameters {
  type: string;
  name: string;
  description: string;
  parameters: {
    contract_types?: string[];
    allow_equals?: boolean;
    maturity_types?: string[];
    duration_units?: string[];
    min_duration?: Record<string, number>;
    max_duration?: Record<string, number>;
    min_stake?: number;
    max_stake?: number;
    min_payout?: number;
    max_payout?: number;
  };
  available_instruments: string[];
}

export interface ProductCategory {
  name: string;
  display_name: string;
  description: string;
  products: ProductType[];
}

export interface ProductsResponse {
  products: ProductType[];
}

export interface ProductCategoriesResponse {
  categories: ProductCategory[];
}

export interface ProductWithDetails extends ProductParameters {
  // Additional detailed information
}

class ProductService {
  private productCache = new Map<string, ProductParameters>();
  private productsCache: ProductType[] | null = null;
  private categoriesCache: ProductCategory[] | null = null;

  /**
   * Get all available product types
   */
  async getProducts(): Promise<ProductType[]> {
    if (this.productsCache) {
      return this.productsCache;
    }

    try {
      const response = await apiClient.get<ProductsResponse>('/v1/products');
      this.productsCache = response.products;
      return response.products;
    } catch (error) {
      console.error('Failed to fetch products:', error);
      throw new Error('Failed to load available products');
    }
  }

  /**
   * Get product categories with their associated product types
   */
  async getProductCategories(): Promise<ProductCategory[]> {
    if (this.categoriesCache) {
      return this.categoriesCache;
    }

    try {
      const response = await apiClient.get<ProductCategoriesResponse>('/v1/products/categories');
      this.categoriesCache = response.categories;
      return response.categories;
    } catch (error) {
      console.error('Failed to fetch product categories:', error);
      throw new Error('Failed to load product categories');
    }
  }

  /**
   * Get detailed parameters and available instruments for a specific product type
   */
  async getProductParameters(productType: string): Promise<ProductParameters> {
    // Check cache first
    if (this.productCache.has(productType)) {
      return this.productCache.get(productType)!;
    }

    try {
      const response = await apiClient.get<ProductParameters>(`/v1/products/${productType}`);
      
      // Cache the result
      this.productCache.set(productType, response);
      
      return response;
    } catch (error) {
      console.error(`Failed to fetch product parameters for ${productType}:`, error);
      throw new Error(`Failed to load parameters for ${productType}`);
    }
  }

  /**
   * Get available instruments for a specific product type
   */
  async getAvailableInstruments(productType: string): Promise<string[]> {
    try {
      const productParams = await this.getProductParameters(productType);
      return productParams.available_instruments || [];
    } catch (error) {
      console.error(`Failed to get available instruments for ${productType}:`, error);
      return [];
    }
  }

  /**
   * Check if an instrument is available for a specific product type
   */
  async isInstrumentAvailable(productType: string, instrumentId: string): Promise<boolean> {
    try {
      const availableInstruments = await this.getAvailableInstruments(productType);
      return availableInstruments.includes(instrumentId);
    } catch (error) {
      console.error(`Failed to check instrument availability:`, error);
      return false;
    }
  }

  /**
   * Filter instruments by product compatibility
   */
  async filterInstrumentsByProduct(productType: string, instruments: any[]): Promise<any[]> {
    try {
      const availableInstruments = await this.getAvailableInstruments(productType);
      
      if (availableInstruments.length === 0) {
        // If no specific instruments are defined, allow all instruments
        return instruments;
      }
      
      return instruments.filter(instrument => 
        availableInstruments.includes(instrument.instrumentId || instrument.id)
      );
    } catch (error) {
      console.error(`Failed to filter instruments by product:`, error);
      // On error, return all instruments to avoid breaking the UI
      return instruments;
    }
  }

  /**
   * Get UI-formatted product categories for the ProductSelector component
   */
  async getUIProductCategories(): Promise<UIProductCategory[]> {
    try {
      const categories = await this.getProductCategories();
      
      return categories.map(category => ({
        id: category.name,
        name: category.name,
        displayName: category.display_name,
        description: category.description,
        products: category.products.map(product => ({
          id: product.type,
          type: product.type,
          name: product.name,
          description: product.description,
          category: product.category,
          isAvailable: true,
          popularityRank: this.getProductPopularityRank(product.type)
        }))
      }));
    } catch (error) {
      console.error('Failed to get UI product categories:', error);
      
      // Return fallback categories
      return this.getFallbackUICategories();
    }
  }

  /**
   * Get detailed product information with parameters
   */
  async getProductWithDetails(productType: string): Promise<ProductWithDetails> {
    try {
      const productParams = await this.getProductParameters(productType);
      return productParams as ProductWithDetails;
    } catch (error) {
      console.error(`Failed to get product details for ${productType}:`, error);
      throw new Error(`Failed to load details for ${productType}`);
    }
  }

  /**
   * Get popularity ranking for products (for UI sorting)
   */
  private getProductPopularityRank(productType: string): number {
    const rankings = {
      'rise_fall': 1,
      'higher_lower': 2,
      'matches_differs': 3,
      'even_odd': 4,
      'over_under': 5,
      'accumulator': 6
    };
    return rankings[productType as keyof typeof rankings] || 99;
  }

  /**
   * Fallback UI categories when API is unavailable
   */
  private getFallbackUICategories(): UIProductCategory[] {
    return [
      {
        id: 'directional',
        name: 'directional',
        displayName: 'Directional',
        description: 'Options that predict market direction relative to entry or barrier levels',
        products: [
          {
            id: 'rise_fall',
            type: 'rise_fall',
            name: 'Rise/Fall',
            description: 'Predict whether the market will rise or fall from its current level.',
            category: 'directional',
            isAvailable: true,
            popularityRank: 1
          },
          {
            id: 'higher_lower',
            type: 'higher_lower',
            name: 'Higher/Lower',
            description: 'Predict whether the market will end higher or lower than a specified barrier.',
            category: 'directional',
            isAvailable: true,
            popularityRank: 2
          }
        ]
      },
      {
        id: 'digit-based',
        name: 'digit-based',
        displayName: 'Digits',
        description: 'Options based on predictions about the last digit of the spot price',
        products: [
          {
            id: 'matches_differs',
            type: 'matches_differs',
            name: 'Matches/Differs',
            description: 'Predict whether the last digit of the spot price will match or differ from a specific digit.',
            category: 'digit-based',
            isAvailable: true,
            popularityRank: 3
          },
          {
            id: 'even_odd',
            type: 'even_odd',
            name: 'Even/Odd',
            description: 'Predict whether the last digit of the spot price will be even or odd.',
            category: 'digit-based',
            isAvailable: true,
            popularityRank: 4
          },
          {
            id: 'over_under',
            type: 'over_under',
            name: 'Over/Under',
            description: 'Predict whether the last digit of the spot price will be over or under a specified digit.',
            category: 'digit-based',
            isAvailable: true,
            popularityRank: 5
          }
        ]
      },
      {
        id: 'growth-based',
        name: 'growth-based',
        displayName: 'Growth',
        description: 'Options that accumulate profits based on market conditions',
        products: [
          {
            id: 'accumulator',
            type: 'accumulator',
            name: 'Accumulator',
            description: 'Accumulate profits as long as the market stays within a certain range.',
            category: 'growth-based',
            isAvailable: true,
            popularityRank: 6
          }
        ]
      }
    ];
  }

  /**
   * Clear cache (useful for testing or when data needs to be refreshed)
   */
  clearCache(): void {
    this.productCache.clear();
    this.productsCache = null;
    this.categoriesCache = null;
  }
}

export const productService = new ProductService();
